import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import {
  Button,
  Tabs,
  Pagination,
  SearchInput,
  Tooltip,
  TooltipProvider,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DatePicker,
  DateRangePicker,
  type CalendarRange,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  SortableHeader,
  EmptyState,
  ToastViewport,
} from "@/components/ui";
import { useUrlState } from "@/lib/useUrlState";
import { emitToast, type ToastTone } from "@/lib/toast";
import { ChevronDown, Info, Inbox, Settings } from "@/icons";
import type { SortState } from "@/lib/table";
import { buildHead } from "@/lib/seo";

export const Route = createFileRoute("/composites")({
  head: () => buildHead({ path: "/composites", title: "Composites (dev)", noindex: true }),
  validateSearch: (s: Record<string, unknown>): { tab?: string; page?: number; q?: string } => ({
    tab: typeof s.tab === "string" ? s.tab : undefined,
    page: s.page != null ? Number(s.page) : undefined,
    q: typeof s.q === "string" ? s.q : undefined,
  }),
  component: Composites,
});

const ROWS = [
  { market: "BTC > 100k", side: "YES", price: 6200 },
  { market: "ETH > 5k", side: "NO", price: 4100 },
  { market: "Fed cut in March", side: "YES", price: 7300 },
  { market: "World Cup: Brazil", side: "YES", price: 1800 },
];

function Composites() {
  const [tab, setTab] = useUrlState("tab", "overview");
  const [page, setPage] = useUrlState("page", 0);
  const [q, setQ] = useUrlState("q", "");
  const [date, setDate] = useState<Date | null>(null);
  const [range, setRange] = useState<CalendarRange>({});
  const [checked, setChecked] = useState(true);
  const [sort, setSort] = useState<SortState>({ by: undefined, dir: "asc" });

  const rows = useMemo(() => {
    const r = [...ROWS];
    if (sort.by === "price") r.sort((a, b) => (sort.dir === "asc" ? a.price - b.price : b.price - a.price));
    if (sort.by === "market")
      r.sort((a, b) => (sort.dir === "asc" ? a.market.localeCompare(b.market) : b.market.localeCompare(a.market)));
    return r;
  }, [sort]);

  const fireToast = (kind: ToastTone) =>
    emitToast({
      kind,
      title: `${kind[0].toUpperCase()}${kind.slice(1)} toast`,
      body: "Subscribed from the lib/toast bus — no UI import.",
      dedupKey: `demo-${kind}`,
    });

  return (
    <TooltipProvider>
      <div className="min-h-dvh bg-bg text-fg">
        <div className="mx-auto max-w-4xl px-5 py-10">
          <header className="mb-6">
            <h1 className="font-display text-2xl font-bold tracking-tight">Composites</h1>
            <p className="text-sm text-fg-muted">Phase 4 — overlays + URL-state. Tab/page/search live in the URL ↑</p>
          </header>

          <Tabs
            value={tab}
            onValueChange={setTab}
            className="mb-5"
            items={[
              { value: "overview", label: "Overview" },
              { value: "table", label: "Table", count: rows.length },
              { value: "empty", label: "Empty" },
            ]}
          />

          {tab === "overview" && (
            <div className="space-y-6">
              <Section title="Overlays">
                <div className="flex flex-wrap items-center gap-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="primary">Open dialog</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Confirm trade</DialogTitle>
                        <DialogDescription>This places a market order immediately.</DialogDescription>
                      </DialogHeader>
                      <DialogBody>Radix gives focus-trap, ESC, scroll-lock + ARIA for free.</DialogBody>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="ghost">Cancel</Button>
                        </DialogClose>
                        <DialogClose asChild>
                          <Button>Confirm</Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline">Bottom sheet</Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="pb-6">
                      <SheetHeader>
                        <SheetTitle>Trade panel</SheetTitle>
                      </SheetHeader>
                      <div className="px-5 text-sm text-fg-muted">Slides up from the bottom on mobile.</div>
                    </SheetContent>
                  </Sheet>

                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline">Right sheet</Button>
                    </SheetTrigger>
                    <SheetContent side="right">
                      <SheetHeader>
                        <SheetTitle>Filters</SheetTitle>
                      </SheetHeader>
                      <div className="px-5 text-sm text-fg-muted">Desktop side panel.</div>
                    </SheetContent>
                  </Sheet>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary">
                        Menu <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuLabel>Account</DropdownMenuLabel>
                      <DropdownMenuItem>
                        <Settings className="h-4 w-4" /> Settings
                      </DropdownMenuItem>
                      <DropdownMenuCheckboxItem checked={checked} onCheckedChange={setChecked}>
                        Live updates
                      </DropdownMenuCheckboxItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-danger data-[highlighted]:bg-danger/10">Log out</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost">Popover</Button>
                    </PopoverTrigger>
                    <PopoverContent>
                      <p className="text-sm text-fg-muted">Anchored, dismissable content.</p>
                    </PopoverContent>
                  </Popover>

                  <Tooltip content="Helpful hint">
                    <Button size="icon" variant="outline" aria-label="Info">
                      <Info className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                </div>
              </Section>

              <Section title="Toasts">
                <div className="flex flex-wrap gap-2">
                  {(["success", "info", "warn", "danger"] as ToastTone[]).map((k) => (
                    <Button key={k} variant="outline" size="sm" onClick={() => fireToast(k)} className="capitalize">
                      {k}
                    </Button>
                  ))}
                </div>
              </Section>

              <Section title="Date pickers">
                <div className="grid max-w-md gap-3">
                  <DatePicker value={date} onChange={setDate} />
                  <DateRangePicker value={range} onChange={setRange} />
                </div>
              </Section>

              <Section title="Search (URL-synced)">
                <div className="max-w-md">
                  <SearchInput value={q} onChange={setQ} placeholder="Search markets…" />
                </div>
              </Section>
            </div>
          )}

          {tab === "table" && (
            <div className="space-y-4">
              <Table>
                <THead>
                  <TR>
                    <SortableHeader label="Market" field="market" sort={sort} onSortChange={setSort} />
                    <TH>Side</TH>
                    <SortableHeader label="Price" field="price" sort={sort} onSortChange={setSort} align="right" />
                  </TR>
                </THead>
                <TBody>
                  {rows.map((r) => (
                    <TR key={r.market} className="hover:bg-surface-2">
                      <TD className="font-medium">{r.market}</TD>
                      <TD className={r.side === "YES" ? "text-success" : "text-danger"}>{r.side}</TD>
                      <TD className="text-right tabular-nums">{(r.price / 100).toFixed(2)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
              <Pagination page={page} totalPages={8} onPage={setPage} />
            </div>
          )}

          {tab === "empty" && (
            <EmptyState
              icon={<Inbox />}
              title="No positions yet"
              description="When you place a trade it'll show up here."
              action={<Button>Explore markets</Button>}
            />
          )}
        </div>
        <ToastViewport />
      </div>
    </TooltipProvider>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-fg-muted">{title}</h2>
      {children}
    </section>
  );
}
